import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ClipboardCheck } from 'lucide-react'
import { getInventoryItems, postValuationWriteDown } from '../../api/inventory'
import ErrorBanner from '../../components/ErrorBanner'
import { useToast } from '../../components/Toast.jsx'
import { money, num } from '../sales/salesShared'
import BinNav from './BinNav'
import BinScan from './BinScan'
import ItemPicker from './ItemPicker'
import { binLabel, qty, stockItems, unitCost, useAllAreas, useBins, useItemBins, useLocations } from './inventoryShared'

// Count one item in one bin. A difference is recorded the same way as
// Adjust stock (found extra or missing), so the bin, the total and the
// books all follow. Stock that is simply in another bin should be moved.
export default function BinAdjustBySku() {
  const { notify } = useToast()
  const [params] = useSearchParams()
  const locations = useLocations()
  const allAreas = useAllAreas()
  const [items, setItems] = useState([])
  const [productId, setProductId] = useState(params.get('product_id') || '')
  const [loc, setLoc] = useState('')
  const [areaId, setAreaId] = useState('')
  const [counted, setCounted] = useState('')
  const [askApproval, setAskApproval] = useState(false)
  const [version, setVersion] = useState(0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => { getInventoryItems().then((d) => setItems(stockItems(d))).catch(() => setItems([])) }, [version])
  useEffect(() => { if (locations?.length && !loc) setLoc(String(locations[0].location_id)) }, [locations, loc])
  // A bin given in the address (from "Check a bin") opens at its location.
  useEffect(() => {
    const a = allAreas.find((x) => String(x.area_id) === String(params.get('area_id')))
    if (a) { setLoc(String(a.location_id)); setAreaId(String(a.area_id)) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allAreas])

  const item = items.find((p) => String(p.product_id) === String(productId))
  const bins = useBins(loc)
  const itemBins = useItemBins(item?.sku, version)
  const bin = bins.find((b) => String(b.area_id) === String(areaId))
  const expected = num((itemBins?.rows || []).find((r) => String(r.area_id) === String(areaId))?.qty)
  const hasCount = String(counted).trim() !== '' && Number.isFinite(Number(counted)) && Number(counted) >= 0
  const diff = hasCount ? Number(counted) - expected : 0
  const cost = unitCost(item)

  // Start on a bin that should hold the item, unless one is already chosen
  // or scanned (counting a bin item by item keeps the same bin).
  useEffect(() => {
    if (areaId) return
    const holding = (itemBins?.rows || []).filter((r) => String(r.location_id) === String(loc) && num(r.qty) > 0)
    if (holding.length) setAreaId(String(holding[0].area_id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemBins, loc])
  const hasBins = allAreas.some((a) => a.level === 'Bin')

  const save = async () => {
    setError('')
    if (!item) { setError('Choose the product.'); return }
    if (!bin) { setError('Choose the bin you counted.'); return }
    if (!hasCount) { setError('Enter how many you counted.'); return }
    if (Math.abs(diff) < 1e-9) { notify('The count matches. Nothing to change.'); return }
    if (cost <= 0) { setError('This product has no cost yet, so the difference can’t be recorded. Receive it first.'); return }
    setSaving(true)
    try {
      const res = await postValuationWriteDown({
        product_id: item.product_id,
        reason: diff > 0 ? 'overage' : 'theft',
        qty: Math.abs(diff),
        location_id: Number(loc),
        area_id: Number(bin.area_id),
        require_approval: askApproval || undefined,
      })
      const pending = String(res?.status || '').toLowerCase() === 'draft'
      notify(pending
        ? `Sent for approval: ${qty(Math.abs(diff))} ${diff > 0 ? 'extra' : 'missing'} in ${bin.path}`
        : `${bin.path} now holds ${qty(counted)} ${item.name || item.sku}`)
      setCounted('')
      setAskApproval(false)
      setVersion((v) => v + 1)
    } catch (e) {
      setError(e.message || 'Could not record the count.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="ci-page">
      <BinNav title="Count a bin" meta="Count a product in one bin. Any difference is recorded as found or missing stock, and the books follow." />

      {error && <ErrorBanner message={error} />}

      <div className="ci-form-meta-card">
        <ItemPicker items={items} value={productId} onChange={(id) => { setProductId(id); setCounted('') }} />
      </div>

      <div className="ci-form-meta-card">
        <div className={`ci-form-meta-top ${locations?.length > 1 ? 'three' : 'two'}`}>
          {locations?.length > 1 && (
            <label className="ci-form-field">
              <span>Location</span>
              <select value={loc} onChange={(e) => { setLoc(e.target.value); setAreaId('') }}>
                {locations.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
              </select>
            </label>
          )}
          <div className="inv-bin-field">
            <label className="ci-form-field">
              <span>Bin</span>
              <select value={areaId} onChange={(e) => setAreaId(e.target.value)}>
                <option value="">{bins.length ? 'Choose a bin…' : 'No bins here yet'}</option>
                {bins.map((b) => <option key={b.area_id} value={b.area_id}>{binLabel(b)}</option>)}
              </select>
            </label>
            <BinScan
              areas={allAreas}
              locationId={loc}
              onPick={(a) => { setLoc(String(a.location_id)); setAreaId(String(a.area_id)); setCounted('') }}
              validate={(a) => (hasBins && a.level !== 'Bin' ? `That’s ${a.level === 'Aisle' ? 'an' : 'a'} ${a.level.toLowerCase()} label (${a.path}). Scan a bin.` : '')}
              placeholder="Or scan the bin label"
            />
          </div>
          <label className="ci-form-field">
            <span>Counted{item && bin ? ` (should be ${qty(expected)})` : ''}</span>
            <input value={counted} onChange={(e) => setCounted(e.target.value)} inputMode="decimal" placeholder="0" disabled={!item || !bin} />
          </label>
        </div>
        {item && bin && hasCount && (
          Math.abs(diff) < 1e-9
            ? <p className="doc-hint">The count matches. Nothing to change.</p>
            : (
              <p className="doc-note">
                {diff > 0
                  ? `${qty(diff)} more than expected: records ${qty(diff)} found in ${bin.path} and a ${money(diff * cost)} gain.`
                  : `${qty(-diff)} fewer than expected: records ${qty(-diff)} missing from ${bin.path} and a ${money(-diff * cost)} loss.`}
                {' '}If {diff > 0 ? 'they came' : 'they’re'} from another bin, <Link to={`/inventory/bin-move-sku?mode=move&product_id=${item.product_id}`}>move them</Link> instead.
              </p>
            )
        )}
        <label className="ci-form-toggle">
          <input type="checkbox" checked={askApproval} onChange={(e) => setAskApproval(e.target.checked)} />
          Ask an admin to approve this first
        </label>
      </div>

      <div className="ci-form-action-bar">
        <div className="ci-form-action-left">
          <button type="button" className="ci-btn ci-btn-primary" disabled={saving || !item || !bin || !hasCount} onClick={save}>
            <ClipboardCheck size={14} />{saving ? 'Saving…' : (askApproval ? 'Send for approval' : 'Save count')}
          </button>
        </div>
      </div>
    </div>
  )
}
